import { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { adminApi } from '../../lib/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { StatusPill } from '../../components/StatusPills';
import {
  GitMerge, Search, ArrowRight, Undo2, Loader2,
} from 'lucide-react';
import { toast } from 'sonner';

const FIELD_LABELS = {
  name: 'Name',
  type: 'Type',
  privacy_email: 'Privacy email',
  correspondence_email: 'Correspondence email',
  address: 'Address',
  website: 'Website',
  parent_org_id: 'Parent org',
  admin_notes: 'Admin notes',
};

/* ------------------------------------------------------------------ */
/* OrgPicker — small async-search box that returns a canonical org    */
/* ------------------------------------------------------------------ */
const OrgPicker = ({ value, onPick, label, exclude, testIdPrefix }) => {
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);

  useEffect(() => {
    const t = setTimeout(async () => {
      if (q.trim().length < 2) {
        setResults([]);
        return;
      }
      try {
        const res = await adminApi.listOrgs({ q: q.trim(), limit: 8 });
        setResults((res.data?.results || []).filter((r) => r.id !== exclude));
        setOpen(true);
      } catch {
        setResults([]);
      }
    }, 200);
    return () => clearTimeout(t);
  }, [q, exclude]);

  useEffect(() => {
    const handle = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, []);

  return (
    <div className="space-y-1.5">
      <Label className="text-sm">{label}</Label>
      {value ? (
        <div
          className="rounded-lg bg-ground px-3 py-2.5 flex items-center justify-between"
          data-testid={`${testIdPrefix}-selected`}
        >
          <div className="min-w-0">
            <p className="text-sm font-medium text-foreground truncate">{value.name}</p>
            <p className="text-xs text-muted-foreground truncate">
              {value.type} · {value.privacy_email || 'no email'}
              {value.admin_verified ? '' : ' · unverified'}
            </p>
          </div>
          <Button variant="ghost" size="sm" onClick={() => onPick(null)} data-testid={`${testIdPrefix}-clear`}>
            Change
          </Button>
        </div>
      ) : (
        <div className="relative" ref={wrapperRef}>
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by name, alias or email"
            className="pl-9"
            data-testid={`${testIdPrefix}-input`}
          />
          {open && results.length > 0 && (
            <div className="absolute z-40 mt-1 w-full bg-surface border border-rule rounded-xl shadow-overlay max-h-64 overflow-y-auto">
              {results.map((r) => (
                <button
                  key={r.id}
                  type="button"
                  onClick={() => {
                    onPick(r);
                    setQ('');
                    setOpen(false);
                  }}
                  className="w-full text-left px-3 py-2 hover:bg-ground text-sm"
                  data-testid={`${testIdPrefix}-result-${r.id}`}
                >
                  <div className="font-medium text-foreground truncate">{r.name}</div>
                  <div className="text-xs text-muted-foreground truncate">
                    {r.type} · {r.privacy_email || 'no email'}
                    {!r.admin_verified && ' · unverified'}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* DiffRow — one field with three radio choices                       */
/* ------------------------------------------------------------------ */
const DiffRow = ({ field, source, target, choice, onChange }) => {
  const same = (source || null) === (target || null);
  return (
    <div
      className={`grid sm:grid-cols-[140px_1fr_1fr] gap-3 py-2.5 border-b border-rule last:border-0 ${
        same ? 'opacity-60' : ''
      }`}
      data-testid={`merge-diff-${field}`}
    >
      <div className="text-xs text-muted-foreground uppercase tracking-wide self-center">
        {FIELD_LABELS[field] || field}
        {same && <span className="ml-1 text-[10px]">(same)</span>}
      </div>
      <label
        className={`rounded-lg border p-2.5 cursor-pointer transition-colors duration-state ${
          choice === 'source' ? 'border-brand bg-brand-wash/50' : 'border-transparent bg-ground hover:bg-inset'
        }`}
      >
        <input
          type="radio"
          checked={choice === 'source'}
          onChange={() => onChange('source')}
          className="sr-only"
          data-testid={`merge-diff-${field}-source`}
        />
        <p className="text-[10px] text-muted-foreground uppercase tracking-wide">Source</p>
        <p className="text-sm text-foreground break-words mt-0.5 whitespace-pre-wrap">
          {source || <span className="text-muted-foreground italic">empty</span>}
        </p>
      </label>
      <label
        className={`rounded-lg border p-2.5 cursor-pointer transition-colors duration-state ${
          choice === 'target' ? 'border-brand bg-brand-wash/50' : 'border-transparent bg-ground hover:bg-inset'
        }`}
      >
        <input
          type="radio"
          checked={choice === 'target'}
          onChange={() => onChange('target')}
          className="sr-only"
          data-testid={`merge-diff-${field}-target`}
        />
        <p className="text-[10px] text-muted-foreground uppercase tracking-wide">Target (winner by default)</p>
        <p className="text-sm text-foreground break-words mt-0.5 whitespace-pre-wrap">
          {target || <span className="text-muted-foreground italic">empty</span>}
        </p>
      </label>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* MergeTool                                                           */
/* ------------------------------------------------------------------ */
const AdminMerge = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [source, setSource] = useState(null);
  const [target, setTarget] = useState(null);
  const [preview, setPreview] = useState(null);
  const [overrides, setOverrides] = useState({});
  const [executing, setExecuting] = useState(false);
  const [recent, setRecent] = useState([]);
  const [done, setDone] = useState(null);

  // Read ?source=&target= (e.g. when arrived from Candidates "Merge into this")
  useEffect(() => {
    const sId = searchParams.get('source');
    const tId = searchParams.get('target');
    if (!sId && !tId) return;
    (async () => {
      try {
        if (sId && (!source || source.id !== sId)) {
          const res = await adminApi.getOrg(sId);
          setSource(res.data);
        }
        if (tId && (!target || target.id !== tId)) {
          const res = await adminApi.getOrg(tId);
          setTarget(res.data);
        }
        // Clear URL params once consumed so refresh doesn't reset
        setSearchParams({}, { replace: true });
      } catch {
        // non-fatal — user can pick manually
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadPreview = useCallback(async () => {
    if (!source || !target) {
      setPreview(null);
      return;
    }
    try {
      const res = await adminApi.mergePreview(source.id, target.id);
      setPreview(res.data);
      // Default each field to 'target' (winner-by-convention)
      const seed = {};
      (res.data?.diff || []).forEach((d) => {
        seed[d.field] = 'target';
      });
      setOverrides(seed);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load merge preview');
      setPreview(null);
    }
  }, [source, target]);

  const loadRecent = useCallback(async () => {
    try {
      const res = await adminApi.mergeRecent();
      setRecent(res.data?.results || []);
    } catch {
      // non-fatal
    }
  }, []);

  useEffect(() => {
    loadPreview();
  }, [loadPreview]);
  useEffect(() => {
    loadRecent();
  }, [loadRecent]);

  const execute = async () => {
    if (!source || !target) return;
    setExecuting(true);
    try {
      const res = await adminApi.mergeExecute({
        source_id: source.id,
        target_id: target.id,
        field_overrides: overrides,
      });
      toast.success(`Merged. ${res.data?.user_orgs_repointed || 0} user records re-pointed.`);
      setDone(res.data);
      setSource(null);
      setTarget(null);
      setPreview(null);
      loadRecent();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Merge failed');
    } finally {
      setExecuting(false);
    }
  };

  const restore = async (sourceId) => {
    try {
      await adminApi.mergeRestore(sourceId);
      toast.success('Restored');
      loadRecent();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Restore failed');
    }
  };

  const conflictCount = preview?.diff?.filter((d) => !d.same).length || 0;

  return (
    <div className="space-y-5" data-testid="admin-merge-page">
      <div>
        <h1 className="font-heading text-2xl font-semibold text-foreground">Merge tool</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Combine duplicate canonical records. The target wins by default — switch any field to source where appropriate.
        </p>
      </div>

      {done && (
        <Card className="bg-brand-wash text-brand-ink">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="text-sm">
              <p className="font-medium">Merge complete</p>
              <p>
                {done.user_orgs_repointed} user records re-pointed. Source can be restored within 30 days.
              </p>
            </div>
            <Button
              size="sm"
              variant="secondary"
              className="ml-auto"
              onClick={() => setDone(null)}
              data-testid="merge-done-dismiss"
            >
              Dismiss
            </Button>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent className="p-5 space-y-4">
          <div className="grid md:grid-cols-[1fr_auto_1fr] gap-4 items-end">
            <OrgPicker
              value={source}
              onPick={setSource}
              label="Source — will be soft-deleted after merge"
              exclude={target?.id}
              testIdPrefix="merge-source"
            />
            <ArrowRight className="hidden md:block w-5 h-5 text-muted-foreground mb-3 mx-auto" />
            <OrgPicker
              value={target}
              onPick={setTarget}
              label="Target — surviving record"
              exclude={source?.id}
              testIdPrefix="merge-target"
            />
          </div>
        </CardContent>
      </Card>

      {preview && (
        <Card>
          <CardContent className="p-5 space-y-3">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div>
                <p className="font-heading text-base font-semibold text-foreground">Field-by-field diff</p>
                <p className="text-xs text-muted-foreground">
                  {conflictCount} conflicting field{conflictCount === 1 ? '' : 's'} ·{' '}
                  {preview.source_linked_users} source user{preview.source_linked_users === 1 ? '' : 's'} will be re-pointed
                </p>
              </div>
              <StatusPill tone="neutral" size="sm">Aliases will be combined</StatusPill>
            </div>
            <div className="rounded-lg border border-rule p-3">
              {preview.diff.map((d) => (
                <DiffRow
                  key={d.field}
                  field={d.field}
                  source={d.source}
                  target={d.target}
                  choice={overrides[d.field]}
                  onChange={(v) => setOverrides({ ...overrides, [d.field]: v })}
                />
              ))}
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <Button variant="secondary" onClick={() => { setSource(null); setTarget(null); }}>
                Cancel
              </Button>
              <Button onClick={execute} disabled={executing} data-testid="merge-execute-btn">
                {executing ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <GitMerge className="w-4 h-4 mr-1.5" />}
                Execute merge
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {recent.length > 0 && (
        <Card>
          <CardContent className="p-5 space-y-3">
            <p className="font-heading text-base font-semibold text-foreground">Recent merges (restorable)</p>
            <ul className="space-y-2">
              {recent.map((r) => (
                <li
                  key={r.id}
                  className="flex items-center justify-between gap-3 rounded-lg bg-ground px-3 py-2 text-sm"
                  data-testid={`merge-recent-${r.id}`}
                >
                  <div className="min-w-0">
                    <p className="font-medium text-foreground truncate">{r.name}</p>
                    <p className="text-xs text-muted-foreground truncate">
                      Merged into {r.target_name || '—'} ·{' '}
                      {new Date(r.deleted_at).toLocaleDateString()}
                    </p>
                  </div>
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => restore(r.id)}
                    data-testid={`merge-restore-${r.id}`}
                  >
                    <Undo2 className="w-4 h-4 mr-1.5" /> Restore
                  </Button>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default AdminMerge;
